/**
 * Age range display utilities for Play Dates.
 *
 * Ages are stored in months internally. These helpers convert
 * month values into human-friendly labels like "Newborn",
 * "6 months", "1 year", "2 years", etc.
 */

/** Common age milestones used in the age-range selectors. */
export const AGE_MILESTONES = [
  { value: 0, label: "0–3 months" },
  { value: 4, label: "4–6 months" },
  { value: 7, label: "7–9 months" },
  { value: 10, label: "10–12 months" },
  { value: 13, label: "1–2 years" },
  { value: 24, label: "2–3 years" },
  { value: 36, label: "3–4 years" },
  { value: 48, label: "4–5 years" },
] as const;

/**
 * Convert a month value to a readable string.
 */
export function formatMonths(months: number): string {
  if (months === 0) return "Newborn";
  if (months < 12) return `${months} months`;
  if (months === 12) return "1 year old";
  if (months < 24) return `${months} months`;
  const years = Math.round(months / 12);
  return `${years} year${years === 1 ? "" : "s"} old`;
}

/**
 * Format an age range for display (legacy, kept for backward compat).
 */
export function formatAgeRange(
  minMonths: number | null | undefined,
  maxMonths: number | null | undefined,
): string | null {
  const hasMin = minMonths != null;
  const hasMax = maxMonths != null;

  if (!hasMin && !hasMax) return null;

  if (hasMin && hasMax) {
    return `${formatMonths(minMonths)} – ${formatMonths(maxMonths)}`;
  }

  if (hasMin) {
    return `${formatMonths(minMonths)}+`;
  }

  return `Up to ${formatMonths(maxMonths!)}`;
}

/**
 * Format children ages for display on a play date card.
 * e.g. "1 child — 2 years" or "2 children — 6 months, 2 years"
 */
export function formatChildrenSummary(childrenAges: number[]): string | null {
  if (!childrenAges || childrenAges.length === 0) return null;
  const count = childrenAges.length;
  const ages = childrenAges.map(formatMonths).join(", ");
  return `${count} child${count > 1 ? "ren" : ""} — ${ages}`;
}
